import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import { Autoplay, Pagination, Navigation, EffectCoverflow } from 'swiper/modules';
import { 
  FaFire, 
  FaMedal, 
  FaGem, 
  FaHeart, 
  FaEye, 
  FaMapMarkerAlt, 
  FaRecycle,
  FaBox
} from 'react-icons/fa';
import { getMediaUrl } from '../utils/urlHelpers';
import { 
  hasActivePromotion,
  getPromotionPriority,
  filterPromotedProducts
} from '../utils/promotionUtils';
import '../styles/premium-carousel.css';
import error from '../assets/Tr3.jpg';

// Premium Carousel Item Component
const PremiumCarouselItem = React.memo(({
  product,
  index,
  onProductClick,
  handleLikeClick,
  isLiked
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const itemRef = useRef(null);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
  }, []);

  const handleImageError = useCallback((e) => {
    console.error('Premium carousel image failed to load:', e.target.src);
    setImageLoaded(false);
    setImageError(true);
    e.target.src = error;
  }, []);

  const handleLocalLikeClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof handleLikeClick === 'function') {
      await handleLikeClick(product.id, isLiked);
    }
  };

  const formatPrice = (price) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    
    if (typeof numPrice !== 'number' || isNaN(numPrice) || numPrice < 0) {
      return '0';
    }
    
    if (numPrice >= 1000) {
      return `${(numPrice / 1000).toLocaleString('fr-FR', {
        maximumFractionDigits: 1,
        minimumFractionDigits: 0
      })}k`;
    }
    return numPrice.toLocaleString('fr-FR', {
      maximumFractionDigits: 0
    });
  };

  const formattedPrice = formatPrice(product.price);
  const priceDisplay = product.negotiable
    ? `À partir de ${product.currency} ${formattedPrice}`
    : `${product.currency} ${formattedPrice}`;

  return (
    <div 
      ref={itemRef}
      className="premium-carousel-item"
      onClick={() => onProductClick(product.uuid)}  // Use product.uuid
    >
      {/* Premium border */}
      <div className="premium-card-border">
        <div className="premium-card-inner">
          {/* Image Section */}
          <div className="premium-image-container">
            {(!imageLoaded || imageError) && (
              <div className="premium-image-placeholder">
                <div className="premium-shimmer-overlay" />
                <FaGem className="premium-placeholder-icon" />
              </div>
            )}
            
            {product.images && product.images.length > 0 && !imageError && (
              <div 
                className={`premium-image-wrapper ${imageLoaded ? 'loaded' : ''}`}
              >
                <img
                  src={
                    product.first_image
                      ? getMediaUrl(product.first_image)
                      : getMediaUrl(product.images[0].url || product.images[0].image)
                  }
                  alt={product.name}
                  className="premium-image"
                  loading="lazy"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              </div>
            )}

            {imageError && (
              <div className="premium-no-image">
                <span>Premium Content</span>
              </div>
            )}
          </div>

          {/* Premium overlay - simplified like CustomCarousel */}
          <div className="premium-overlay">
            {/* Price Overlay - clean and simple */}
            <div className="premium-price-overlay">
              {priceDisplay}
            </div>

            {/* Condition Badge */}
            <div className={`premium-condition-badge ${product.condition === 'Usagé' ? 'used' : 'new'}`}>
              {product.condition === 'Usagé' ? (
                <FaRecycle />
              ) : (
                <FaBox />
              )}
            </div>
          </div>

          {/* Gradient overlay for content like in mobile app */}
          <div className="premium-gradient-overlay">
            {/* Product Name */}
            <h3 className="premium-product-name">
              {product.name}
            </h3>

            {/* Tags Section - like mobile app */}
            <div className="premium-tags">
              {/* Location Tag */}
              <div className="premium-tag premium-location-tag">
                <FaMapMarkerAlt className="icon" />
                <span>{product.city_name}</span>
              </div>
            </div>

            {/* Stats - like mobile app */}
            <div className="premium-stats">
              <div
                className={`premium-stat-item premium-likes ${isLiked ? 'liked' : ''}`}
                onClick={handleLocalLikeClick}
              >
                <FaHeart className="icon" />
                <span>{product.likes || 0}</span>
              </div>
              <div className="premium-stat-item premium-views">
                <FaEye className="icon" />
                <span>{product.views || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Main Premium Carousel Component
const PremiumCarousel = ({ 
  products = [], 
  loggedInUser, 
  onProductClick, 
  handleLikeClick,
  likedProducts = [],
  hasMore = false,
  loading = false,
  fetchMoreData,
  title = "Produits Premium",
  subtitle = "Sélection exclusive de nos partenaires"
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef(null);

  // Filter promoted products only
  const promotedProducts = filterPromotedProducts(products);

  const likedProductIds = useMemo(() => {
    const ids = new Set();

    if (Array.isArray(likedProducts)) {
      likedProducts.forEach((id) => ids.add(id));
    }

    if (loggedInUser && Array.isArray(promotedProducts)) {
      promotedProducts.forEach((product) => {
        if (product?.liked_by?.includes?.(loggedInUser.id)) {
          ids.add(product.id);
        }
      });
    }

    return ids;
  }, [likedProducts, promotedProducts, loggedInUser]);

  const handleLocalLikeClick = async (productId, isLikedByUser) => {
    if (typeof handleLikeClick === 'function') {
      await handleLikeClick(productId, isLikedByUser);
    }
  };

  const handleSlideChange = (swiper) => {
    setActiveIndex(swiper.activeIndex);
  };

  const shouldShowNavigation = promotedProducts.length > 1;

  // Don't render if no promoted products
  if (!promotedProducts || promotedProducts.length === 0) {
    return null;
  }

  return (
    <div className="premium-carousel-container">
      {/* Elegant header */}
      <div className="premium-header">
        <div className="premium-header-content">
          <div className="premium-header-icon">
            <FaGem />
          </div>
          <h2 className="premium-header-title">{title}</h2>
          <div className="premium-header-line" />
        </div>
        <p className="premium-header-subtitle">{subtitle}</p>
      </div>

      {/* Premium Swiper Carousel */}
      <div className="premium-swiper-container">
        <Swiper
          ref={swiperRef}
          modules={[Autoplay, Pagination, Navigation, EffectCoverflow]}
          spaceBetween={20}
          slidesPerView={1.5}
          centeredSlides={false}
          loop={promotedProducts.length > 2}
          autoplay={{
            delay: 4000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          pagination={{
            clickable: true,
            el: '.premium-pagination',
            bulletClass: 'premium-pagination-bullet',
            bulletActiveClass: 'premium-pagination-bullet-active',
          }}
          navigation={{
            nextEl: shouldShowNavigation ? '.premium-button-next' : null,
            prevEl: shouldShowNavigation ? '.premium-button-prev' : null,
          }}
          onSlideChange={handleSlideChange}
          breakpoints={{
            // Mobile phones (2.5 products visible)
            320: {
              slidesPerView: 2.5,
              spaceBetween: 12,
              centeredSlides: false,
            },
            // Large phones (2.5 products visible)
            480: {
              slidesPerView: 2.5,
              spaceBetween: 15,
              centeredSlides: false,
            },
            // Tablets
            768: {
              slidesPerView: 2.8,
              spaceBetween: 20,
              centeredSlides: false,
            },
            // Small laptops
            1024: {
              slidesPerView: 3.2,
              spaceBetween: 25,
              centeredSlides: false,
            },
            // Desktops
            1280: {
              slidesPerView: 3.5,
              spaceBetween: 30,
              centeredSlides: false,
            },
            // Large screens
            1536: {
              slidesPerView: 4.2,
              spaceBetween: 35,
              centeredSlides: false,
            },
          }}
          className="premium-swiper"
        >
          {promotedProducts.map((product, index) => (
            <SwiperSlide key={product.id} className="premium-swiper-slide">
              <PremiumCarouselItem
                product={product}
                index={index}
                onProductClick={onProductClick}
                handleLikeClick={handleLocalLikeClick}
                isLiked={likedProductIds.has(product.id)}
              />
            </SwiperSlide>
          ))}

          {/* Loading slide if fetching more */}
          {hasMore && loading && (
            <SwiperSlide className="premium-swiper-slide">
              <div className="premium-loading-slide">
                <div className="premium-loading-spinner" />
                <span>Chargement...</span>
              </div>
            </SwiperSlide>
          )}
        </Swiper>

        {/* Custom Navigation */}
        {shouldShowNavigation && (
          <>
            <div className="premium-button-prev premium-nav-button">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </div>
            <div className="premium-button-next premium-nav-button">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
              </svg>
            </div>
          </>
        )}

        {/* Custom Pagination */}
        <div className="premium-pagination" />
      </div>
    </div>
  );
};

export default PremiumCarousel;
